import { useCallback, useMemo, useRef, useState } from 'react';
import axios from 'axios';

// Links a screen's SavedFilterTabs and ColumnPicker so a saved tab can carry
// its own column set (saved_filters.visible_columns). The screen creates one
// of these and passes it to both components as `tabColumns`:
//
//   const tabColumns = useTabColumns(OPTIONAL_COLUMNS, SYSTEM_COLUMNS);
//   <ColumnPicker ... tabColumns={tabColumns} />
//   <SavedFilterTabs ... tabColumns={tabColumns} />
//
// `activeTab` is the selected saved tab ({ id, visible_columns }) or null
// on Default/system tabs. A tab with visible_columns null follows the
// user's screen-level preference, so the picker saves toggles there; a tab
// with its own array gets toggles saved to the tab instead.
//
// `systemColumns` (optional): labels of the screen's always-shown columns,
// so the tab drawer's Columns section lists them the same way the picker
// does (SystemColumnRows).
function useTabColumns(columns, systemColumns) {
  const [activeTab, setActiveTab] = useState(null);
  // Column sets saved from the picker since SavedFilterTabs last fetched
  // its rows — so reselecting a tab shows what was saved, not the stale
  // row SavedFilterTabs still holds.
  const overridesRef = useRef(new Map());
  // Whatever the picker is currently showing — the drawer pre-fills a
  // tab's own column list from it.
  const currentVisibleRef = useRef(null);

  const selectTab = useCallback((tab) => {
    if (!tab) {
      setActiveTab(null);
      return;
    }
    const overrides = overridesRef.current;
    setActiveTab({
      id: tab.id,
      visible_columns: overrides.has(tab.id) ? overrides.get(tab.id) : (tab.visible_columns ?? null),
    });
  }, []);

  // A tab row fresh from the server (after a drawer save) replaces any
  // column set the picker saved to it earlier.
  const tabSaved = useCallback((tab) => {
    overridesRef.current.delete(tab.id);
    setActiveTab({ id: tab.id, visible_columns: tab.visible_columns ?? null });
  }, []);

  const saveActiveTabColumns = useCallback((next) => {
    if (!activeTab) return;
    overridesRef.current.set(activeTab.id, next);
    setActiveTab({ ...activeTab, visible_columns: next });
    axios.put(`/api/saved-filters/${activeTab.id}/columns`, { visible_columns: next }).catch(() => {});
  }, [activeTab]);

  const setCurrentVisible = useCallback((visible) => {
    currentVisibleRef.current = visible;
  }, []);

  const getCurrentVisible = useCallback(() => currentVisibleRef.current || columns.map((c) => c.key), [columns]);

  return useMemo(() => ({
    columns,
    systemColumns,
    activeTab,
    selectTab,
    tabSaved,
    saveActiveTabColumns,
    setCurrentVisible,
    getCurrentVisible,
  }), [columns, systemColumns, activeTab, selectTab, tabSaved, saveActiveTabColumns, setCurrentVisible, getCurrentVisible]);
}

export default useTabColumns;
